import React, { useState } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import {
  CalendarMonthOutlined as CalendarIcon,
  KeyboardArrowDown as ChevronIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { chart } from '../../theme/chart';
import { addMonths, monthKey, monthLabel } from '../../utils/calendar';

interface Props {
  /** the month in focus, YYYY-MM */
  value: string;
  onChange: (month: string) => void;
  /** how many months the menu offers, the current one included */
  span?: number;
  now?: Date;
}

/**
 * The home's "when": a pill naming the month in focus that opens the last
 * twelve months. Every card below reads the month it picks.
 */
export const MonthPicker: React.FC<Props> = ({ value, onChange, span = 12, now = new Date() }) => {
  const { t, i18n } = useTranslation('dashboard');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const options = Array.from({ length: span }, (_, back) => addMonths(now, -back)).map((date) => ({
    key: monthKey(date),
    label: monthLabel(date, i18n.language),
  }));
  const label = options.find((option) => option.key === value)?.label
    ?? monthLabel(new Date(`${value}-01T12:00:00`), i18n.language);

  const pick = (month: string) => {
    setAnchor(null);
    if (month !== value) onChange(month);
  };

  return (
    <>
      <Button
        onClick={(event) => setAnchor(event.currentTarget)}
        title={t('home.monthPicker') as string}
        aria-haspopup="listbox"
        aria-expanded={Boolean(anchor)}
        startIcon={<CalendarIcon sx={{ fontSize: '16px !important' }} />}
        endIcon={<ChevronIcon sx={{ fontSize: '18px !important' }} />}
        sx={{
          height: 36,
          px: 1.75,
          borderRadius: 999,
          border: `1px solid ${chart.grid}`,
          bgcolor: chart.surface,
          color: chart.ink,
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          '&:hover': { bgcolor: chart.surface, borderColor: chart.inkMuted },
          '& .MuiButton-startIcon': { mr: 1, color: chart.inkSecondary },
          '& .MuiButton-endIcon': { ml: 0.75, color: chart.inkSecondary },
        }}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        MenuListProps={{ role: 'listbox', dense: true, 'aria-label': t('home.monthPicker') as string }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{ sx: { mt: 1, borderRadius: 2, minWidth: 200 } }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.key}
            role="option"
            selected={option.key === value}
            aria-selected={option.key === value}
            onClick={() => pick(option.key)}
            sx={{ fontSize: 13 }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
